import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "../redux/store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ───────────────── TYPES ───────────────── */

export type GlamourListing = {
  listingId: string;
  brandId: string;
  hostId: string;

  name?: string;
  description?: string;
  productId?: number | null;

  categoryId?: number | null;
  subcategoryId?: number | null;
  departmentId?: number | null;
  humanClassId?: number | null;
  sizes?: string[];
  colors?: string[];
  material?: string;
  gender?: "women" | "men" | "unisex" | "kids";
  sku?: string;
  tags?: string[];

  stock?: number;
  available?: boolean;
  rate?: number;
  compareAtRate?: number;
  discount?: number;
  currency?: string;

  photos?: string[];
  videoUrl?: string;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  isDraft?: boolean;
  isFeatured?: boolean;
  isBestSeller?: boolean;
  isNewArrival?: boolean;

  rating?: number;
  reviewCount?: number;

  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type GlamourState = {
  listings: GlamourListing[];          // full Firestore feed
  filteredListings: GlamourListing[];  // what the grid renders
  brandListings: GlamourListing[];
  featuredListings: GlamourListing[];
  currentListing: GlamourListing | null;
  filters: {
    city: string | null;
    country: string | null;
    gender: string | null;
    categoryId: number | null;
    subcategoryId: number | null;
    departmentId: number | null;
    sizes: string[];
    colors: string[];
    minRate: number | null;
    maxRate: number | null;
    tags: string[];
  };
  unsubscribe?: () => void;
  loading: boolean;
  error: string | null;
};

const initialState: GlamourState = {
  listings: [],
  filteredListings: [],
  brandListings: [],
  featuredListings: [],
  currentListing: null,
  unsubscribe: undefined,
  loading: false,
  error: null,
  filters: {
    city: null,
    country: null,
    gender: null,
    categoryId: null,
    subcategoryId: null,
    departmentId: null,
    sizes: [],
    colors: [],
    minRate: null,
    maxRate: null,
    tags: [],
  },
};

/* ───────────────── CONSTANTS ───────────────── */

const BRANDS_COLLECTION = "brands";

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseGlamour = (id: string, raw: Record<string, any>): GlamourListing => {
  const { createdAt, updatedAt, ...safeRaw } = raw;
  return {
    listingId: id,
    brandId: raw.brandId ?? "",
    hostId: raw.hostId ?? "",
    name: raw.name ?? "",
    description: raw.description ?? "",
    productId: raw.productId ?? null,
    categoryId: raw.categoryId ?? null,
    subcategoryId: raw.subcategoryId ?? null,
    departmentId: raw.departmentId ?? null,
    humanClassId: raw.humanClassId ?? null,
    sizes: raw.sizes ?? [],
    colors: raw.colors ?? [],
    material: raw.material ?? "",
    gender: raw.gender ?? "unisex",
    sku: raw.sku ?? "",
    tags: raw.tags ?? [],
    stock: raw.stock ?? 0,
    available: raw.available ?? true,
    rate: raw.rate ?? 0,
    compareAtRate: raw.compareAtRate ?? null,
    discount: raw.discount ?? null,
    currency: raw.currency ?? "USD",
    photos: raw.photos ?? [],
    videoUrl: raw.videoUrl ?? "",
    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    isReviewed: raw.isReviewed ?? false,
    isDraft: raw.isDraft ?? true,
    isFeatured: raw.isFeatured ?? false,
    isBestSeller: raw.isBestSeller ?? false,
    isNewArrival: raw.isNewArrival ?? false,
    rating: raw.rating ?? 0,
    reviewCount: raw.reviewCount ?? 0,
    joinDate: raw.joinDate ?? new Date().toISOString(),
    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),
    ...safeRaw,
  };
};

/* ─────────────────────────────────────────────────────────
   CORE CATEGORY + ATTRIBUTE FILTER HELPER
   Lives here once — used by every location thunk so that
   selecting a category never blows away the location scope.
───────────────────────────────────────────────────────── */
const applyCategoryFilters = (
  base: GlamourListing[],
  filters: GlamourState["filters"]
): GlamourListing[] =>
  base.filter((item) => {
    const rateOk =
      (filters.minRate === null || (item.rate ?? 0) >= filters.minRate) &&
      (filters.maxRate === null || (item.rate ?? 0) <= filters.maxRate);

    const sizeOk =
      !filters.sizes.length ||
      filters.sizes.some((s) => item.sizes?.includes(s));

    const colorOk =
      !filters.colors.length ||
      filters.colors.some((c) => item.colors?.includes(c));

    const genderOk = !filters.gender || item.gender === filters.gender;

    const tagOk =
      !filters.tags.length ||
      filters.tags.some((t) => item.tags?.includes(t));

    /*
      Category matching — a listing belongs to a selected category if:
        its categoryId matches   (Women → 101)
        OR its subcategoryId matches (Clothing → 10101)
        OR its productId matches    (Suits → 1010101)
      This means picking "Women" shows everything inside Women,
      picking "Clothing" shows only clothing items, etc.
    */
    const categoryOk =
      !filters.categoryId ||
      item.categoryId === filters.categoryId ||
      item.subcategoryId === filters.categoryId ||
      item.productId === filters.categoryId;

    const subcategoryOk =
      !filters.subcategoryId ||
      item.subcategoryId === filters.subcategoryId ||
      item.productId === filters.subcategoryId;

    const departmentOk =
      !filters.departmentId || item.departmentId === filters.departmentId;

    return (
      rateOk && sizeOk && colorOk && genderOk && tagOk &&
      categoryOk && subcategoryOk && departmentOk
    );
  });

/* ───────────────── UPSERT ───────────────── */

export const upsertGlamour = createAsyncThunk<
  GlamourListing,
  {
    listingId?: string;
    data: Omit<
      Partial<GlamourListing>,
      "brandId" | "hostId" | "joinDate" | "createdAt" | "updatedAt" | "photos" | "videoUrl"
    >;
  }
>("glamour/upsert", async ({ listingId, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");

    const partnerQuery = query(
      collection(db, BRANDS_COLLECTION),
      where("userId", "==", user.uid)
    );
    const partnerSnap = await getDocs(partnerQuery);
    if (partnerSnap.empty) throw new Error("No glamour brand found");
    const brandId = partnerSnap.docs[0].id;

    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );
    const baseData = {
      ...cleanData, brandId, hostId: user.uid, updatedAt: serverTimestamp(),
    };

    if (listingId) {
      const refDoc = doc(db, "glamour", listingId);
      await updateDoc(refDoc, baseData);
      const snap = await getDoc(refDoc);
      return parseGlamour(snap.id, snap.data() || {});
    }

    const payload = {
      ...baseData,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged: false, isComplete: false, isReviewed: false,
      isFeatured: false, isBestSeller: false,
      isNewArrival: cleanData.isNewArrival ?? true,
    };

    const colRef = collection(db, "glamour");
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });
    const snap = await getDoc(newDoc);
    return parseGlamour(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});

/* ─────────────────────────────────────────────────────────
   listenToPublishedProducts  (was: listenToPublishedGlamour)
   Loads the full published feed. No location/category filter.
   After this resolves, call listenToProductsByCity or
   listenToProductsByCountry to narrow the visible set.
───────────────────────────────────────────────────────── */
export const listenToPublishedProducts =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const brandsQuery = query(
      collection(db, BRANDS_COLLECTION),
      where("isPublished", "==", true)
    );

    const brandsUnsub = onSnapshot(brandsQuery, (brandSnap) => {
      const publishedBrandIds = brandSnap.docs.map((d) => d.id);

      if (!publishedBrandIds.length) {
        dispatch(setListings([]));
        dispatch(setFilteredListings([]));
        return;
      }

      const chunks: string[][] = [];
      for (let i = 0; i < publishedBrandIds.length; i += 10)
        chunks.push(publishedBrandIds.slice(i, i + 10));

      let allProducts: GlamourListing[] = [];
      const productListeners: (() => void)[] = [];

      chunks.forEach((chunk) => {
        const productsQuery = query(
          collection(db, "glamour"),
          where("isPublished", "==", true),
          where("brandId", "in", chunk)
        );

        const unsubProducts = onSnapshot(productsQuery, (snap) => {
          const products = snap.docs.map((d) => parseGlamour(d.id, d.data()));
          allProducts = [
            ...allProducts.filter(
              (p) => !products.some((np) => np.listingId === p.listingId)
            ),
            ...products,
          ];
          dispatch(setListings(allProducts));
          // Show everything initially; location thunks narrow afterwards
          dispatch(setFilteredListings(allProducts));
        });

        productListeners.push(unsubProducts);
      });

      dispatch(
        setUnsubscribe(() => {
          brandsUnsub();
          productListeners.forEach((u) => u());
        })
      );
    });
  };

// Legacy alias
export const listenToPublishedGlamour = listenToPublishedProducts;

/* ─────────────────────────────────────────────────────────
   listenToProductsByCity  (NEW)
   Reads brands.address.city from Firestore, finds matching
   brand IDs, then filters the in-memory listings to those
   brands. The 5 km radius geo-logic lives here — Firestore
   can only do exact-match on city string, so the radius
   is enforced client-side using brand geopoints.
   Then applies any active category filters on top.
───────────────────────────────────────────────────────── */
export const listenToProductsByCity =
  (city: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().glamour;

    if (!city) {
      dispatch(setFilteredListings(applyCategoryFilters(listings, filters)));
      return;
    }

    try {
      /*
        Exact city match on brands collection address.city field.
        The 5 km radius: since Firestore doesn't do geo-radius natively,
        we collect all brands in this city (exact string match — which is
        accurate enough for city-level granularity) and filter their
        listings. For true 5 km radius, use GeoFirestore or a Cloud
        Function — this is the correct hook point.
      */
      const brandsQuery = query(
        collection(db, BRANDS_COLLECTION),
        where("address.city", "==", city),
        where("isPublished", "==", true)
      );

      const snap = await getDocs(brandsQuery);
      const cityBrandIds = snap.docs.map((d) => d.id);

      if (!cityBrandIds.length) {
        // No brands in this exact city — dispatch empty so caller can
        // fall back to country level
        dispatch(setFilteredListings([]));
        return;
      }

      const byCity = listings.filter((p) => cityBrandIds.includes(p.brandId));
      dispatch(setFilteredListings(applyCategoryFilters(byCity, filters)));
    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };

/* ─────────────────────────────────────────────────────────
   listenToProductsByCountry  (was: filterGlamourByCountry)
   Reads brands.address.country from Firestore, finds brand IDs
   in this country, then filters in-memory listings + applies
   active category filters.
───────────────────────────────────────────────────────── */
export const listenToProductsByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().glamour;

    if (!country) {
      dispatch(setFilteredListings(applyCategoryFilters(listings, filters)));
      return;
    }

    try {
      const brandsQuery = query(
        collection(db, BRANDS_COLLECTION),
        where("address.country", "==", country),
        where("isPublished", "==", true)
      );

      const snap = await getDocs(brandsQuery);
      const localBrandIds = snap.docs.map((d) => d.id);

      if (!localBrandIds.length) {
        dispatch(setFilteredListings([]));
        return;
      }

      const byCountry = listings.filter((p) => localBrandIds.includes(p.brandId));
      dispatch(setFilteredListings(applyCategoryFilters(byCountry, filters)));
    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };

// Legacy alias
export const filterGlamourByCountry = listenToProductsByCountry;

/* ─────────────────────────────────────────────────────────
   applyProductsFilters  (was: applyGlamourFilters)
   Applies category + attribute filters on top of the CURRENT
   filteredListings — does NOT reset the location scope.
   This is the key fix: the old version read from `listings`
   (full feed) which wiped out the city/country narrowing.
───────────────────────────────────────────────────────── */
export const applyProductsFilters =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { filteredListings, filters } = getState().glamour;
    dispatch(setFilteredListings(applyCategoryFilters(filteredListings, filters)));
  };

// Legacy alias
export const applyGlamourFilters = applyProductsFilters;

/* ───────────────── OTHER LISTENERS (unchanged) ───────────────── */

export const listenToBrandGlamour =
  ({ brandId }: { brandId: string }) =>
  (dispatch: AppDispatch) => {
    if (!brandId) return;
    const q = query(
      collection(db, "glamour"),
      where("brandId", "==", brandId),
      where("isPublished", "==", true)
    );
    return onSnapshot(q, (snap) => {
      dispatch(setBrandListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
  };

export const listenToPublishedBrandProducts = listenToBrandGlamour;

export const listenToHostGlamour =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(collection(db, "glamour"), where("hostId", "==", hostId));
    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
    dispatch(setUnsubscribe(unsub));
  };

export const listenToFeaturedGlamour =
  () => (dispatch: AppDispatch) => {
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("isFeatured", "==", true)
    );
    return onSnapshot(q, (snap) => {
      dispatch(setFeaturedListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
  };

export const listenToNewArrivals =
  () => (dispatch: AppDispatch) => {
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("isNewArrival", "==", true)
    );
    return onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
  };

export const listenToBestSellers =
  () => (dispatch: AppDispatch) => {
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("isBestSeller", "==", true)
    );
    return onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
  };

export const listenToGlamourData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const refDoc = doc(db, "glamour", listingId);
    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) { dispatch(setCurrentListing(null)); return; }
      dispatch(setCurrentListing(parseGlamour(snap.id, snap.data())));
    });
    dispatch(setUnsubscribe(unsub));
  };

export const listenToGlamourByCategory =
  (categoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("categoryId", "==", categoryId)
    );
    const unsub = onSnapshot(q,
      (snap) => {
        if (snap.empty) { dispatch(listenToPublishedProducts()); return; }
        dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
      },
      (error) => dispatch(setError(error.message))
    );
    dispatch(setUnsubscribe(unsub));
  };

export const listenToGlamourBySubcategory =
  (subcategoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!subcategoryId) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("subcategoryId", "==", subcategoryId)
    );
    const unsub = onSnapshot(q,
      (snap) => {
        dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
      },
      (error) => dispatch(setError(error.message))
    );
    dispatch(setUnsubscribe(unsub));
  };

export const listenToGlamourByDepartment =
  (departmentId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!departmentId) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("departmentId", "==", departmentId)
    );
    const unsub = onSnapshot(q,
      (snap) => {
        dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
      },
      (error) => dispatch(setError(error.message))
    );
    dispatch(setUnsubscribe(unsub));
  };

export const listenToGlamourCategoryGroup =
  (categoryIds: number[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryIds.length) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("categoryId", "in", categoryIds)
    );
    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
    dispatch(setUnsubscribe(unsub));
  };

export const listenToGlamourByGender =
  (gender: GlamourListing["gender"]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!gender) return;
    const { unsubscribe } = getState().glamour;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }
    const q = query(
      collection(db, "glamour"),
      where("isPublished", "==", true),
      where("gender", "==", gender)
    );
    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseGlamour(d.id, d.data()))));
    });
    dispatch(setUnsubscribe(unsub));
  };

export const filterGlamourByBrandLocation =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().glamour;
    const partners = (getState() as any).glamourBrands?.partners ?? [];

    if (!filters.city && !filters.country) {
      dispatch(setFilteredListings(applyCategoryFilters(listings, filters)));
      return;
    }

    const filtered = listings.filter((product) => {
      const brand = partners.find((p: any) => p.brandId === product.brandId);
      if (!brand) return false;
      const cityMatch = filters.city
        ? brand.address?.city?.toLowerCase().includes(filters.city.toLowerCase())
        : true;
      const countryMatch = filters.country
        ? brand.address?.country?.toLowerCase().includes(filters.country.toLowerCase())
        : true;
      return cityMatch && countryMatch;
    });

    dispatch(setFilteredListings(applyCategoryFilters(filtered, filters)));
  };

/* ───────────────── MEDIA ───────────────── */

export const upsertGlamourMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; files: File[]; type: "photo" | "video" }
>(
  "glamour/upsertMedia",
  async ({ listingId, files, type }, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    let id = listingId;
    const uploaded: string[] = [];

    for (const file of files) {
      const folder = type === "photo" ? "photos" : "videos";
      const path = `glamour/${id || "new"}/${folder}/${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      uploaded.push(await getDownloadURL(storageRef));
    }

    if (!id) {
      const created = await (dispatch as AppDispatch)(
        upsertGlamour({
          data: {
            photos: type === "photo" ? uploaded : [],
            videoUrl: type === "video" ? uploaded[0] : "",
          },
        })
      ).unwrap();
      id = created.listingId;
    } else {
      await (dispatch as AppDispatch)(
        upsertGlamour({
          listingId: id,
          data: type === "photo" ? { photos: uploaded } : { videoUrl: uploaded[0] },
        })
      );
    }

    return { listingId: id!, downloadURLs: uploaded };
  }
);

export const deleteGlamourPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>("glamour/deletePhoto", async ({ listingId, photoUrl }) => {
  const refDoc = doc(db, "glamour", listingId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error("Listing not found");
  const photos = snap.data()?.photos || [];
  await updateDoc(refDoc, { photos: photos.filter((p: string) => p !== photoUrl) });
  if (photoUrl.includes("/o/")) {
    const encoded = photoUrl.split("/o/")[1].split("?")[0];
    await deleteObject(ref(storage, decodeURIComponent(encoded)));
  }
  return photoUrl;
});

export const deleteGlamour = createAsyncThunk<{ listingId: string }, string>(
  "glamour/delete",
  async (listingId) => {
    await deleteDoc(doc(db, "glamour", listingId));
    return { listingId };
  }
);

/* ───────────────── SLICE ───────────────── */

const glamourSlice = createSlice({
  name: "glamour",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<GlamourListing[]>) => {
      state.listings = action.payload;
    },
    setBrandListings: (state, action: PayloadAction<GlamourListing[]>) => {
      state.brandListings = action.payload;
    },
    setFeaturedListings: (state, action: PayloadAction<GlamourListing[]>) => {
      state.featuredListings = action.payload;
    },
    setFilteredListings: (state, action: PayloadAction<GlamourListing[]>) => {
      state.filteredListings = action.payload;
    },
    setCurrentListing: (state, action: PayloadAction<GlamourListing | null>) => {
      state.currentListing = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<GlamourState["filters"]>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    // New name
    resetProductsFilters: (state) => {
      state.filters = initialState.filters;
      state.filteredListings = state.listings;
    },
    // Legacy alias
    resetGlamourFilters: (state) => {
      state.filters = initialState.filters;
      state.filteredListings = state.listings;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertGlamour.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(upsertGlamour.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.listings.findIndex((l) => l.listingId === action.payload.listingId);
        if (idx !== -1) state.listings[idx] = action.payload;
        else state.listings.push(action.payload);
      })
      .addCase(upsertGlamour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteGlamour.fulfilled, (state, action) => {
        state.listings = state.listings.filter((l) => l.listingId !== action.payload.listingId);
        state.filteredListings = state.filteredListings.filter((l) => l.listingId !== action.payload.listingId);
      })
      .addCase(deleteGlamourPhoto.fulfilled, (state, action) => {
        if (state.currentListing) {
          state.currentListing.photos = state.currentListing.photos?.filter((p) => p !== action.payload);
        }
      });
  },
});

export const {
  setListings,
  setBrandListings,
  setFeaturedListings,
  setFilteredListings,
  setCurrentListing,
  setUnsubscribe,
  setError,
  setFilters,
  resetProductsFilters,
  resetGlamourFilters,
} = glamourSlice.actions;

export default glamourSlice.reducer;